import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert
} from '@mui/material';
import { Subscription } from '../../types';

interface EditReferenceNameDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (referenceName: string) => Promise<{ success: boolean; error?: { message: string } }>;
  subscription: Subscription | null;
}

const EditReferenceNameDialog: React.FC<EditReferenceNameDialogProps> = ({
  open,
  onClose,
  onSubmit,
  subscription
}) => {
  const [referenceName, setReferenceName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (subscription && open) {
      setReferenceName(subscription.name || '');
      setError(null);
    }
  }, [subscription, open]);

  const handleSubmit = async () => {
    if (!referenceName.trim()) {
      setError('Reference name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await onSubmit(referenceName.trim());
      
      if (result.success) {
        onClose();
        setReferenceName('');
      } else {
        setError(result.error?.message || 'Failed to update reference name');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setReferenceName('');
      setError(null);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Edit Subscription Name
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <TextField
            label="Reference Name"
            value={referenceName}
            onChange={(e) => setReferenceName(e.target.value)}
            fullWidth
            margin="normal"
            helperText="A friendly name to help you identify this subscription"
            autoFocus
            disabled={isSubmitting}
          />
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || !referenceName.trim()}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditReferenceNameDialog;